import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { getVisitorId } from "@/lib/tracking";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Lock } from "lucide-react";

const PLANS = ["essential", "pro", "premium"];
const ALIASES = { essentiel: "essential" };
const eur = (p) => Number(p).toFixed(2).replace(".", ",");

// Grille des offres payantes (abonnement direct ou conversion après essai). L'offre dans l'URL n'ouvre aucun droit : droits calculés côté serveur.
export function PricingGrid({ onSelect, current, busy, compact = false }) {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); api.post("/track/event", { name: "pricing_viewed", visitor_id: getVisitorId() }).catch(() => {}); }, []);
  return (
    <div className="space-y-4" data-testid="pricing-grid">
      <div className={`grid gap-3 ${compact ? "sm:grid-cols-3" : "md:grid-cols-3"}`}>
        {plans.map((p) => (
          <button key={p.code} onClick={() => onSelect(p.code)} disabled={busy || current === p.code} data-testid={`pricing-select-${p.code}`}
            className={`text-left rounded-2xl border p-5 transition-colors hover:border-blue-500 hover:bg-blue-50/40 disabled:opacity-60 ${p.popular ? "border-blue-400 bg-blue-50/30" : "border-slate-200 bg-white"}`}>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{p.name}{p.popular && <span className="ml-2 text-blue-600">Populaire</span>}</div>
            <div className="font-heading text-2xl font-extrabold mt-1">{p.promo_enabled && p.reference_price != null && <span className="text-base font-normal text-slate-400 line-through mr-2" data-testid={`pricing-ref-${p.code}`}>{eur(p.reference_price)} €</span>}{eur(p.price)} € <span className="text-xs font-normal text-slate-400">{p.price_suffix || "HT / mois"}</span></div>
            {!compact && <p className="text-xs text-slate-500 mt-1">{p.tagline}</p>}
            {!compact && <ul className="mt-3 space-y-1 text-xs text-slate-600">{(p.features || []).slice(0, 5).map((f, i) => <li key={i} className="flex gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> {typeof f === "string" ? f : f.label || f.text}</li>)}</ul>}
            <div className="mt-3 text-xs font-semibold text-blue-700">Choisir {p.name}</div>
            {current === p.code && <div className="mt-2 text-xs text-blue-700 font-medium">Offre actuelle</div>}
          </button>
        ))}
      </div>
      <p className="text-sm text-center text-slate-600 font-medium" data-testid="pricing-direct-note"><Lock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Paiement sécurisé Stripe • Carte bancaire requise • Accès complet à l'offre dès confirmation • Résiliable à tout moment</p>
    </div>
  );
}

export default function ChoosePlan() {
  const { plan } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const mode = new URLSearchParams(window.location.search).get("mode") === "direct" ? "direct" : "trial";
  const go = (code) => {
    api.post("/track/event", { name: "plan_selected", visitor_id: getVisitorId(), props: { plan: code, mode: "direct" } }).catch(() => {});
    api.post("/track/event", { name: "direct_subscription_selected", visitor_id: getVisitorId(), props: { plan: code } }).catch(() => {});
    navigate(user ? `/app/billing/start?plan=${code}` : `/register?plan=${code}&mode=direct`, { replace: !!plan });
  };
  useEffect(() => {
    if (user === null) return;
    if (mode === "trial" && !plan) { api.post("/track/event", { name: "trial_selected", visitor_id: getVisitorId() }).catch(() => {}); navigate(user ? "/app/dashboard" : "/register?mode=trial", { replace: true }); return; }
    if (!plan) return;
    const code = ALIASES[(plan || "").toLowerCase()] || (plan || "").toLowerCase();
    if (!PLANS.includes(code)) { navigate("/choisir?mode=direct", { replace: true }); return; }
    go(code);
  }, [plan, user, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  if (plan || mode === "trial") return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm" data-testid="choose-plan-redirect"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Préparation…</div>;
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="choose-plan-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Choisissez votre offre</h1>
          <p className="text-slate-500 mt-2 text-sm sm:text-base">Abonnement immédiat : création du compte, puis paiement sécurisé Stripe.</p>
          <p className="text-sm mt-3">Vous préférez tester d'abord ? <button onClick={() => navigate("/register?mode=trial")} className="text-blue-600 font-semibold underline" data-testid="choose-mode-trial">Essayer gratuitement 7 jours — sans carte bancaire</button></p>
        </div>
        <PricingGrid onSelect={go} />
        <div className="text-center"><Button variant="ghost" onClick={() => navigate(user ? "/app/dashboard" : "/login")} data-testid="choose-plan-login">{user ? "Retour à mon espace" : "J'ai déjà un compte"}</Button></div>
      </div>
    </div>
  );
}
